import { useState, useEffect } from 'react';
import '../styles/ActionButtons.css';
import './CheckoutModal.css';
import { fetchWithTimeout } from '../utils/fetchWithTimeout';
import { SALON_API_PREFIX } from '../utils/apiConfig';

/**
 * @param {'product'|'treatment'} variant - product: save-placed-order-products; treatment: save-placed-order-treatments
 */
const CheckoutModal = ({
  isOpen,
  onClose,
  cartItems: initialCartItems,
  onCartUpdate,
  sessionId,
  apiBaseUrl,
  token,
  variant = 'product',
}) => {
  const [cartItems, setCartItems] = useState([]);
  const [isPlacingOrder, setIsPlacingOrder] = useState(false);
  const isTreatment = variant === 'treatment';

  useEffect(() => {
    if (isOpen && initialCartItems) {
      setCartItems(initialCartItems);
    }
  }, [isOpen, initialCartItems]);

  const parsePrice = (priceString) => {
    if (priceString == null) return 0;
    const cleaned = priceString.toString().replace(/[^0-9.]/g, '');
    return parseFloat(cleaned) || 0;
  };

  const getUnitPriceNumber = (item) => {
    if (isTreatment) {
      const t = item.treatment;
      if (t && t.amount != null && !Number.isNaN(Number(t.amount))) {
        return Number(t.amount);
      }
      return 0;
    }
    return parsePrice(item.product?.price);
  };

  const getItemTotal = (item) => getUnitPriceNumber(item) * item.quantity;

  const getGrandTotal = () => cartItems.reduce((total, item) => total + getItemTotal(item), 0);

  const formatPrice = (price) => `₹${price.toFixed(2)}`;

  const formatTreatmentUnitPrice = (t) => {
    if (!t || t.amount == null) return '₹0.00';
    const n = Number(t.amount);
    return Number.isNaN(n) ? '₹0.00' : `₹${n.toFixed(0)}`;
  };

  const handleIncreaseQuantity = (index) => {
    setCartItems((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], quantity: updated[index].quantity + 1 };
      if (onCartUpdate) onCartUpdate(convertToCartMap(updated));
      return updated;
    });
  };

  const handleDecreaseQuantity = (index) => {
    setCartItems((prev) => {
      const updated = [...prev];
      if (updated[index].quantity > 1) {
        updated[index] = { ...updated[index], quantity: updated[index].quantity - 1 };
      } else {
        updated.splice(index, 1);
      }
      if (onCartUpdate) onCartUpdate(convertToCartMap(updated));
      return updated;
    });
  };

  const convertToCartMap = (items) => {
    const cartItemsMap = {};
    items.forEach((item) => {
      if (isTreatment) {
        const tid =
          item.treatmentId ||
          (item.treatment && item.treatment.id != null ? `treatment_${item.treatment.id}` : null) ||
          `treatment_${(item.treatment && (item.treatment.treatment || item.treatment.name)) || 'unknown'}`;
        cartItemsMap[tid] = { treatment: item.treatment, quantity: item.quantity };
      } else {
        const productId =
          item.productId ||
          item.product?.product_id ||
          item.product?.id ||
          `${item.product?.product_name}_${item.product?.routine_type || 'unknown'}`;
        cartItemsMap[productId] = { product: item.product, quantity: item.quantity };
      }
    });
    return cartItemsMap;
  };

  const handlePlaceOrder = async () => {
    setIsPlacingOrder(true);
    try {
      const now = new Date();
      const pad2 = (value) => String(value).padStart(2, '0');
      const offsetMinutes = -now.getTimezoneOffset();
      const offsetSign = offsetMinutes >= 0 ? '+' : '-';
      const offsetAbs = Math.abs(offsetMinutes);
      const offsetHour = pad2(Math.floor(offsetAbs / 60));
      const offsetMin = pad2(offsetAbs % 60);
      const localIsoWithOffset =
        `${now.getFullYear()}-${pad2(now.getMonth() + 1)}-${pad2(now.getDate())}` +
        `T${pad2(now.getHours())}:${pad2(now.getMinutes())}:${pad2(now.getSeconds())}` +
        `${offsetSign}${offsetHour}:${offsetMin}`;
      const placedAtPayload = {
        placed_at_local: localIsoWithOffset,
        placed_timezone:
          (typeof Intl !== 'undefined' && Intl.DateTimeFormat().resolvedOptions().timeZone) || 'UTC',
        placed_timezone_offset_minutes: offsetMinutes,
        placed_at_epoch_ms: now.getTime(),
      };

      const baseUrl = (
        apiBaseUrl ||
        (typeof window !== 'undefined' &&
          (window.NYAFAZ_API_URL || window.REACT_APP_API_URL || window.location?.origin)) ||
        ''
      ).replace(/\/$/, '');
      if (sessionId && baseUrl && cartItems.length > 0) {
        if (isTreatment) {
          const placed = cartItems.map((item) => ({
            treatment: item.treatment,
            quantity: item.quantity,
          }));
          const response = await fetchWithTimeout(`${baseUrl}${SALON_API_PREFIX}/save-placed-order-treatments`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              skin_id: sessionId,
              session_id: sessionId,
              ...(token && { app_session_token: token }),
              placed_order_treatments: placed,
              ...placedAtPayload,
            }),
            timeoutMs: 20000,
          });
          if (!response.ok) {
            const errData = await response.json().catch(() => ({}));
            console.warn('Could not save placed order (treatments) for tracking:', errData);
          }
        } else {
          const placedOrderProducts = cartItems.map((item) => ({
            product: item.product,
            quantity: item.quantity,
          }));
          const response = await fetchWithTimeout(`${baseUrl}${SALON_API_PREFIX}/save-placed-order-products`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              skin_id: sessionId,
              session_id: sessionId,
              ...(token && { app_session_token: token }),
              placed_order_products: placedOrderProducts,
              ...placedAtPayload,
            }),
            timeoutMs: 20000,
          });
          if (!response.ok) {
            const errData = await response.json().catch(() => ({}));
            console.warn('Could not save placed order for tracking:', errData);
          }
        }
      }
      alert(`Order placed successfully! Total: ${formatPrice(getGrandTotal())}`);
      onClose();
      window.location.href = 'https://nyafaz.com/';
    } catch (err) {
      console.error('Place order error:', err);
      alert(`Order placed! Total: ${formatPrice(getGrandTotal())}. (Tracking save failed)`);
      onClose();
      window.location.href = 'https://nyafaz.com/';
    } finally {
      setIsPlacingOrder(false);
    }
  };

  const handleClose = () => {
    if (onCartUpdate) onCartUpdate(convertToCartMap(cartItems));
    onClose();
  };

  if (!isOpen) return null;

  const emptyTitle = isTreatment ? 'No treatments in your cart' : 'No products in your cart';
  const emptyMessage = isTreatment ? 'Add treatments to continue' : 'Add products to continue shopping';

  return (
    <div className="checkout-modal-overlay" onClick={handleClose}>
      <div className="checkout-modal-container" onClick={(e) => e.stopPropagation()}>
        <div className="checkout-modal-header">
          <h2 className="checkout-modal-title">Checkout</h2>
          <button
            type="button"
            className="checkout-modal-close-button"
            onClick={handleClose}
            aria-label="Close checkout"
          >
            ×
          </button>
        </div>

        <div className="checkout-modal-content">
          {cartItems.length === 0 ? (
            <div className="checkout-empty-cart">
              <div className="checkout-empty-cart-content">
                <div className="checkout-empty-cart-icon">
                  <svg
                    width="64"
                    height="64"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="9" cy="21" r="1"></circle>
                    <circle cx="20" cy="21" r="1"></circle>
                    <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
                  </svg>
                </div>
                <h3 className="checkout-empty-cart-title">{emptyTitle}</h3>
                <p className="checkout-empty-cart-message">{emptyMessage}</p>
              </div>
            </div>
          ) : (
            <>
              <div className="checkout-modal-items-section">
                <h3 className="checkout-modal-section-title">Order Summary</h3>
                <div className="checkout-modal-items-list">
                  {cartItems.map((item, index) => {
                    const itemTotal = getItemTotal(item);
                    const t = item.treatment;
                    const p = item.product;
                    return (
                      <div key={index} className="checkout-modal-item-card">
                        <div className="checkout-modal-item-image">
                          {isTreatment ? (
                            t?.image_url ? (
                              <img
                                src={t.image_url}
                                alt={t.treatment || t.name || 'Treatment'}
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                }}
                              />
                            ) : (
                              <div className="checkout-modal-item-image-placeholder">
                                <span>No Image</span>
                              </div>
                            )
                          ) : p?.image_url ? (
                            <img
                              src={p.image_url}
                              alt={p.product_name}
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                          ) : (
                            <div className="checkout-modal-item-image-placeholder">
                              <span>No Image</span>
                            </div>
                          )}
                        </div>
                        <div className="checkout-modal-item-details">
                          <h4 className="checkout-modal-item-name">
                            {isTreatment ? t?.treatment || t?.name || 'Treatment' : p?.product_name}
                          </h4>
                          {!isTreatment && p?.description && (
                            <p className="checkout-modal-item-description">{p.description}</p>
                          )}
                          <div className="checkout-modal-item-meta">
                            <div className="checkout-modal-item-price">
                              <span className="checkout-modal-meta-label">{isTreatment ? 'Per session:' : 'Unit Price:'}</span>
                              <span className="checkout-modal-meta-value">
                                {isTreatment
                                  ? formatTreatmentUnitPrice(t)
                                  : p?.price
                                    ? p.price.toString().replace(/\$/g, '₹')
                                    : '₹0.00'}
                              </span>
                            </div>
                            <div className="checkout-modal-item-quantity-controls">
                              <span className="checkout-modal-meta-label">Quantity:</span>
                              <div className="checkout-modal-quantity-counter">
                                <button
                                  type="button"
                                  className="checkout-modal-quantity-button checkout-modal-quantity-decrease"
                                  onClick={() => handleDecreaseQuantity(index)}
                                  aria-label="Decrease quantity"
                                >
                                  −
                                </button>
                                <span className="checkout-modal-quantity-value">{item.quantity}</span>
                                <button
                                  type="button"
                                  className="checkout-modal-quantity-button checkout-modal-quantity-increase"
                                  onClick={() => handleIncreaseQuantity(index)}
                                  aria-label="Increase quantity"
                                >
                                  +
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="checkout-modal-item-total">
                          <span className="checkout-modal-item-total-label">Item Total</span>
                          <span className="checkout-modal-item-total-value">{formatPrice(itemTotal)}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="checkout-modal-summary-section">
                <div className="checkout-modal-summary-card">
                  <h3 className="checkout-modal-summary-title">Order Total</h3>
                  <div className="checkout-modal-summary-details">
                    <div className="checkout-modal-summary-row">
                      <span className="checkout-modal-summary-label">Items ({cartItems.length})</span>
                      <span className="checkout-modal-summary-value">{formatPrice(getGrandTotal())}</span>
                    </div>
                    <div className="checkout-modal-summary-divider"></div>
                    <div className="checkout-modal-summary-row checkout-modal-summary-total">
                      <span className="checkout-modal-summary-label">Total</span>
                      <span className="checkout-modal-summary-value">{formatPrice(getGrandTotal())}</span>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="skin-action-button checkout-modal-place-order-button"
                    onClick={handlePlaceOrder}
                    disabled={isPlacingOrder}
                  >
                    {isPlacingOrder ? 'Placing Order...' : 'Place Order'}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CheckoutModal;
